import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { theme } from './theme/theme';
import { queryClient } from './api';
import { AuthProvider } from './hooks/useAuth';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { Login } from './pages/auth/Login';
import { Home } from './pages/home/Home';
import { Inbox } from './pages/inbox/Inbox';
import { Connectors } from './pages/connectors/Connectors';
import { BotsManagement } from './pages/bots/BotsManagement';
import { PaymentStrategies } from './pages/strategies/PaymentStrategies';
import { AccountsReceivable } from './pages/accountsReceivable/AccountsReceivable';
import { Seguimiento } from './pages/seguimiento/Seguimiento';
import { PaymentProofs } from './pages/paymentProofs/PaymentProofs';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import OnBoardingLayout from './pages/OnBoarding';
import { UserManagementSettings } from './pages/settings/UserManagementSettings';
import { ProductCatalogPublic } from './pages/productCatalog/ProductCatalogPublic';
import { ProductCatalogAdmin } from './pages/productCatalog/ProductCatalogAdmin';
import { CatalogPublicImageEditor } from './pages/productCatalog/CatalogPublicImageEditor';
import { Landing } from './pages/landing/Landing';
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy';
import { AgentFlowList } from './pages/agentFlows/AgentFlowList';
import { AgentFlowEditor } from './pages/agentFlows/AgentFlowEditor';

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <BrowserRouter>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Landing />} />
              <Route path="/landing" element={<Navigate to="/" replace />} />
              <Route path="/privacidad" element={<PrivacyPolicy />} />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Home />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inbox"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Inbox />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route path="/onBoarding" element={<OnBoardingLayout/>} />
              <Route path="/catalog/:companyId/:catalogSlug" element={<ProductCatalogPublic />} />
              <Route
                path="/catalog-edit/:companyId/:catalogSlug"
                element={
                  <ProtectedRoute>
                    <CatalogPublicImageEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/connectors"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Connectors />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bots-management"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <BotsManagement />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategies"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <PaymentStrategies />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts-receivable"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <AccountsReceivable />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/seguimiento"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Seguimiento />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-proofs"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <PaymentProofs />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <UserManagementSettings />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product-catalog"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <ProductCatalogAdmin />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent-flows"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <AgentFlowList />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent-flows/:id/edit"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <AgentFlowEditor />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;

